import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';

import { UserStoreSelectors } from '@hosty-app/app-store';

import { Permission } from '@hosty-web/interfaces';
import { PermissionsService } from '@hosty-web/services';

import { SIDEBAR_ITEMS } from './shell/shell-sidebar/sidebar-items';

/** Always reachable: the profile route carries no permission requirement. */
const FALLBACK_ROUTE = '/profile';

export const canActivateGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  const permissions = inject(PermissionsService);
  const router = inject(Router);
  const store = inject(Store);

  if (!route.data.permission || permissions.checkAccessByPermission(route.data.permission)) {
    return true;
  }

  return store.select(UserStoreSelectors.selectUserProfile).pipe(
    take(1),
    map(() => {
      // Redirect to the first section this user can actually open.
      //
      // This previously sent anyone holding the tasksCalendar permission to
      // /tasks unconditionally — including when /tasks was the route being
      // blocked, which handed the router the URL it had just rejected and span
      // forever.
      //
      // Eligibility is decided by the permission the candidate's *route*
      // declares, evaluated with the same test applied above — not by the
      // sidebar item's permissionTypes, which use different semantics (any-of,
      // and /calendar advertises tasksCalendar while its route requires
      // tasksEnabled). Testing what the guard itself will ask guarantees the
      // target admits us, so the redirect always terminates in one hop.
      const canOpen = (path: string): boolean => {
        const permission = routePermission(router, path);
        return !permission || permissions.checkAccessByPermission(permission);
      };

      const target = SIDEBAR_ITEMS.find(
        (item) => !!item.route && !isSameSection(state.url, item.route) && canOpen(item.route),
      );

      return router.createUrlTree([target?.route ?? FALLBACK_ROUTE]);
    }),
  );
};

/** Guards against redirecting to the section we were just refused. */
const isSameSection = (currentUrl: string, route: string): boolean =>
  currentUrl === route || currentUrl.startsWith(`${route}/`) || currentUrl.startsWith(`${route}?`);

/**
 * The permission the route config declares for a sidebar path, so eligibility is
 * decided by what the guard will actually check rather than a parallel list that
 * can drift out of sync.
 */
const routePermission = (router: Router, path: string): Permission | undefined => {
  // Sidebar entries may point at a nested path (/integrations/integrate) while
  // the permission sits on the section route (integrations).
  const section = path.replace(/^\//, '').split('/')[0];
  const findIn = (routes: Route[]): Permission | undefined => {
    for (const r of routes) {
      if (r.path === section) {
        return r.data?.permission;
      }
      const nested = r.children && findIn(r.children);
      if (nested) {
        return nested;
      }
    }
    return undefined;
  };
  return findIn(router.config);
};
